import { FormEvent, useState } from "react";
import { Container, Form, Button, Row, Col, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./FuelCal.css";

interface Generator {
  name: string;
  slope: number;
  base: number;
}

const generators: Generator[] = [
  { name: "C1", slope: 5.8706, base: 39.21 },
  { name: "C2", slope: 5.8706, base: 39.21 },
  { name: "M1", slope: 5.8706, base: 39.21 },
  { name: "M2", slope: 5.8706, base: 39.21 },
  { name: "M3", slope: 5.8706, base: 39.21 },
  { name: "N1", slope: 5.8706, base: 39.21 },
  { name: "N2", slope: 5.8706, base: 39.21 },
];

export default function FuelCalculationPage() {
  const [loads, setLoads] = useState<{ [key: string]: number }>(Object.fromEntries(generators.map(gen => [gen.name, 0])));
  const [volumes, setVolumes] = useState<{ [key: string]: number }>({
    "A1": 0,
    "A2": 0,
    "B1": 0,
    "B2": 0,
  });
  const [selectedGenerators, setSelectedGenerators] = useState<string[]>([]);
  const [genPercentages, setGenPercentages] = useState<{ [key: string]: number }>({});
  const [genConsumptions, setGenConsumptions] = useState<{ [key: string]: number }>({});
  const [totalConsumption, setTotalConsumption] = useState<number>(0);
  const [backupTime, setBackupTime] = useState<number>(0);

  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (checked) {
      setSelectedGenerators([...selectedGenerators, name]);
    } else {
      setSelectedGenerators(selectedGenerators.filter((g) => g !== name));
    }
  };

  const handleLoadChange = (name: string, value: number) => {
    setLoads({ ...loads, [name]: value });
  };

  const handleVolumeChange = (name: string, value: number) => {
    setVolumes({ ...volumes, [name]: value });
  };

  const calculate = (e: FormEvent) => {
    e.preventDefault();

    const existingVolume = Object.values(volumes).reduce((sum, vol) => sum + vol, 0);

    const percentages: { [key: string]: number } = {};
    const consumptions: { [key: string]: number } = {};

    generators.forEach((gen) => {
      const load = loads[gen.name] || 0;
      const calcPercentage = (load * 100) / 2650;
      percentages[gen.name] = calcPercentage === 0 ? 0 : calcPercentage;
      const consumption = gen.base + gen.slope * percentages[gen.name];
      consumptions[gen.name] = selectedGenerators.includes(gen.name) ? (consumption === 0 ? 0 : consumption) : 0;
    });
    setGenPercentages(percentages);
    setGenConsumptions(consumptions);

    let selectedConsumptionSum = 0;
    selectedGenerators.forEach((name) => {
      selectedConsumptionSum += consumptions[name] || 0;
    });

    setTotalConsumption(selectedConsumptionSum);
    setBackupTime(existingVolume * 1000 / selectedConsumptionSum || 0);

  };

  return (
    <Container className="FuelCalculation d-flex flex-column justify-content-center align-items-center mt-4">
      <Row className="justify-content-center w-100">
        <Col md={12} lg={12}>
          <h2 className="text-center mb-4">油缸情況</h2>
          <Row>
            <Col md={6}>
              <h4 className="text-center mb-3">油缸情況</h4>
              <Table striped bordered hover>
                <tbody>
                  {Object.keys(volumes).map((vol) => (
                    <tr key={vol}>
                      <td>{vol} 油量 (L)</td>
                      <td>
                        <Form.Control
                          type="number"
                          value={volumes[vol] || ""}
                          onChange={(e) => handleVolumeChange(vol, Number(e.target.value) || 0)}
                          placeholder="請輸入油量"
                          required
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h4 className="text-center mb-3">全部系統</h4>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>選擇</th>
                    <th>系統</th>
                    <th>現時負載 (kW)</th>
                    <th>百分比 (%)</th>
                    <th>估算 (L/hr)</th>
                  </tr>
                </thead>
                <tbody>
                  {generators.map((gen) => (
                    <tr key={gen.name}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedGenerators.includes(gen.name)}
                          onChange={(e) => handleCheckboxChange(gen.name, e.target.checked)}
                        />
                      </td>
                      <td>{gen.name}</td>
                      <td>
                        <Form.Control
                          type="number"
                          value={loads[gen.name] || ""}
                          onChange={(e) => handleLoadChange(gen.name, Number(e.target.value) || 0)}
                          placeholder="請輸入負載"
                          required
                        />
                      </td>
                      <td>{genPercentages[gen.name] ? genPercentages[gen.name].toFixed(4) : 0}%</td>
                      <td>{genConsumptions[gen.name] ? genConsumptions[gen.name].toFixed(4) : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
          <div className="button-container mt-4">
            <Button variant="primary" className="w-50" onClick={calculate}>
              計算
            </Button>
          </div>
          <h4 className="text-center mt-4">結果</h4>
          <Table striped bordered hover className="result-table">
            <tbody>
              <tr>
                <td>已選擇的發電機 (L/hr)</td>
                <td>{totalConsumption.toFixed(4)}</td>
              </tr>
              <tr>
                <td>已選擇的發電機備用時間 (hr)</td>
                <td>{backupTime.toFixed(4)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}